/**
 * section-forms — dumb form-body components for the vozidla sales-record
 * sections (vehicle params, equipment, discount, financing). One
 * implementation per section, shared by the edit-modal wrappers
 * (`edit-modal-shared` → `SmlouvaEditModals` / `NabidkaEditModals`) and the
 * create wizard's steps (`NabidkaWizard`).
 *
 * Each body owns its fields, local state, seeding, validation, and payload
 * mapping, and renders `<form id={…_FORM_ID}>` with fields only — the chrome
 * (Modal.Foot / wizard Savebar) supplies the submit button via the HTML
 * `form` attribute (`<Button type="submit" form={…_FORM_ID}>`). Seeding is
 * dirty-guarded: bodies re-seed from fresh `initial` data until the user
 * starts editing; modals remount their content per open (Radix unmounts the
 * closed dialog), so "re-seed on open" keeps working.
 *
 * Where the two surfaces render the same field set with different copy or
 * layout (equipment section chrome, wizard-only placeholders), a variant
 * flag keeps each surface's output unchanged.
 */

import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Field, Fields } from '@/components/Field';
import { FormPanel, SectionHeader } from '@/components/FormPanel';
import { Icon } from '@/components/Icon';
import { SelectInput } from '@/components/SelectInput';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { formatInt } from '@/lib/format';
import { EquipmentPriceFields, sDphFromBez } from './EquipmentPriceFields';
import { FleetPolicyPanel } from './FleetPolicyPanel';
import {
  useDeliveryPlacesQuery,
  useFinancingTypesQuery,
  useVatRatesQuery,
  useVehicleModelsQuery,
  type VatRate,
} from './api';

// ─── Vehicle ────────────────────────────────────────────────────────────────

export const VEHICLE_FORM_ID = 'vozidla-vehicle-form';

export interface VehicleFormValue {
  /** Offer variant only — contracts keep their model immutable. */
  vozidlo_model: number;
  /** Contract variant only — the order number. */
  vcauta: string;
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  barva: string;
  calouneni: string;
  zakladni_cena: number;
  id_dph: number;
}

/**
 * Vehicle-parameter fields, shared by the offer wizard's manual sub-form and
 * both vehicle edit modals. Variants: `offer` = editable model (brand-scoped
 * select), no order number; `contract` = params-only + `vcauta`, model
 * immutable. The wrapper owns the mutation (the wizard picks create vs
 * update); the body hands back the raw `VehicleFormValue`.
 */
export function VehicleFormBody({
  variant,
  brandId = 0,
  hints = false,
  initial,
  busy,
  onSubmit,
}: {
  variant: 'offer' | 'contract';
  /** Offer variant: brand scoping the model select (0 disables it). */
  brandId?: number;
  /** Wizard: legacy example placeholders + required marker on the price. */
  hints?: boolean;
  initial: VehicleFormValue;
  busy: boolean;
  onSubmit: (form: VehicleFormValue) => void;
}) {
  const models = useVehicleModelsQuery(variant === 'offer' ? brandId || null : null);
  const vatRates = useVatRatesQuery();

  const [form, setForm] = useState<VehicleFormValue>(initial);
  const [dirty, setDirty] = useState(false);
  // Re-seed from fresh data until the user starts editing.
  useEffect(() => {
    if (!dirty) setForm(initial);
  }, [initial, dirty]);

  const set = <K extends keyof VehicleFormValue>(k: K, v: VehicleFormValue[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (variant === 'offer' && !form.vozidlo_model) {
      toast.error('Vyberte model.');
      return;
    }
    onSubmit(form);
  };

  return (
    <form id={VEHICLE_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        {variant === 'offer' ? (
          <Field label="Model" required>
            <SelectInput
              value={String(form.vozidlo_model)}
              onChange={(e) => set('vozidlo_model', Number(e.currentTarget.value) || 0)}
              disabled={!brandId || models.isLoading}
            >
              <option value="0">— Vyberte model —</option>
              {(models.data?.models ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nazev_modelu}
                </option>
              ))}
            </SelectInput>
          </Field>
        ) : (
          <Field label="Číslo objednávky">
            <TextInput value={form.vcauta} onChange={(e) => set('vcauta', e.currentTarget.value)} />
          </Field>
        )}
        <Field label="Provedení">
          <TextInput
            value={form.provedeni}
            onChange={(e) => set('provedeni', e.currentTarget.value)}
            placeholder="Trend"
          />
        </Field>
        <Field label="Verze">
          <TextInput
            value={form.verze}
            onChange={(e) => set('verze', e.currentTarget.value)}
            placeholder="kombi"
          />
        </Field>
        <Field label="Motor">
          <TextInput
            value={form.motor}
            onChange={(e) => set('motor', e.currentTarget.value)}
            placeholder={hints ? '1.5 EcoBoost / 110kW' : undefined}
          />
        </Field>
        <Field label="Převodovka">
          <TextInput
            value={form.prevodovka}
            onChange={(e) => set('prevodovka', e.currentTarget.value)}
          />
        </Field>
        <Field label="Barva">
          <TextInput
            value={form.barva}
            onChange={(e) => set('barva', e.currentTarget.value)}
            placeholder={hints ? 'Ruby Red' : undefined}
          />
        </Field>
        <Field label="Čalounění">
          <TextInput
            value={form.calouneni}
            onChange={(e) => set('calouneni', e.currentTarget.value)}
          />
        </Field>
        <Field label="Cena (bez DPH)" required={hints}>
          <TextInput
            type="number"
            value={String(form.zakladni_cena || 0)}
            onChange={(e) => set('zakladni_cena', Number(e.currentTarget.value) || 0)}
            suffix="Kč"
          />
        </Field>
        <Field label="Sazba DPH">
          <SelectInput
            value={String(form.id_dph)}
            onChange={(e) => set('id_dph', Number(e.currentTarget.value) || 0)}
          >
            <option value="0">— Vyberte —</option>
            {(vatRates.data?.rates ?? []).map((r) => (
              <option key={r.id_dph} value={r.id_dph}>
                {r.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
      </Fields>
    </form>
  );
}

// ─── Equipment ──────────────────────────────────────────────────────────────
//
// Original + non-original line-item editors against `vozidla_nabidka_nadvybava`
// + `…_neoriginal` (and the smlouva equivalents). The save endpoints wipe +
// re-insert atomically — there's no per-row delete, the whole array is
// bulk-replaced on submit.

export const EQUIPMENT_FORM_ID = 'vozidla-equipment-form';

export interface OriginalRow {
  doekod: string;
  nazev: string;
  cena: string;
  cenaSDph: string;
  id_dph: number;
}
export interface NonOriginalRow {
  nazev: string;
  cena: string;
  cenaSDph: string;
  id_dph: number;
}

const emptyOriginalRow = (): OriginalRow => ({ doekod: '', nazev: '', cena: '0', cenaSDph: '0.00', id_dph: 0 });
const emptyNonOriginalRow = (): NonOriginalRow => ({ nazev: '', cena: '0', cenaSDph: '0.00', id_dph: 0 });

/** Loose shape both the smlouva and nabídka equipment endpoints satisfy. */
export interface EquipmentItemLike {
  doekod?: string | null;
  nazev: string;
  cena: string | null;
  id_dph?: number | null;
}

export interface EquipmentSaveBody {
  original: { doekod: string; nazev: string; cena: string; id_dph: number }[];
  neoriginal: { nazev: string; cena: string; id_dph: number }[];
}

type EquipmentVariant = 'modal' | 'wizard';

/**
 * Equipment row editors + row state. The `modal` variant renders plain
 * sections with mono headings (dialog chrome carries the description); the
 * `wizard` variant renders the two numbered `FormPanel`s of the Výbava step.
 * Loading/error states stay chrome-side — mount the body once data can seed.
 */
export function EquipmentFormBody({
  variant,
  initial,
  busy,
  onSubmit,
  onRowsChange,
}: {
  variant: EquipmentVariant;
  /** Fresh fetch of the stored equipment — re-seeds rows until the user edits. */
  initial: { original: EquipmentItemLike[]; neoriginal: EquipmentItemLike[] } | undefined;
  busy: boolean;
  onSubmit: (body: EquipmentSaveBody) => void;
  /** Mirrors row state up for chrome-side totals (Modal.Foot meta / wizard aside). */
  onRowsChange?: (original: OriginalRow[], neoriginal: NonOriginalRow[]) => void;
}) {
  const vatRates = useVatRatesQuery();

  const [original, setOriginal] = useState<OriginalRow[]>([]);
  const [neoriginal, setNeoriginal] = useState<NonOriginalRow[]>([]);
  // Re-seed from the server data on every fetch *until the user starts
  // editing*. A one-time `hydrated` guard locked in whatever was first in
  // cache — and on re-entry from the detail page that cache is stale (the
  // detail page observes only the summary query, so the equipment query is
  // invalidated-but-unobserved and never refetched). The stale seed then
  // overwrote the just-saved rows on the next save. Keying off `dirty` lets
  // the background refetch's fresh data replace the stale seed.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!initial || dirty) return;
    const rates = vatRates.data?.rates ?? [];
    const seededOriginal = initial.original.map((it) => ({
      doekod: it.doekod ?? '',
      nazev: it.nazev,
      cena: String(it.cena ?? '0'),
      cenaSDph: sDphFromBez(String(it.cena ?? '0'), it.id_dph ?? 0, rates),
      id_dph: it.id_dph ?? 0,
    }));
    const seededNeoriginal = initial.neoriginal.map((it) => ({
      nazev: it.nazev,
      cena: String(it.cena ?? '0'),
      cenaSDph: sDphFromBez(String(it.cena ?? '0'), it.id_dph ?? 0, rates),
      id_dph: it.id_dph ?? 0,
    }));
    setOriginal(seededOriginal);
    setNeoriginal(seededNeoriginal);
    onRowsChange?.(seededOriginal, seededNeoriginal);
    // `rates` only affects the derived s-DPH mirror, so re-seeding when it
    // lands is correct; `onRowsChange` is a stable-enough mirror callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, dirty, vatRates.data]);

  const changeOriginal = (rows: OriginalRow[]) => {
    setOriginal(rows);
    setDirty(true);
    onRowsChange?.(rows, neoriginal);
  };
  const changeNeoriginal = (rows: NonOriginalRow[]) => {
    setNeoriginal(rows);
    setDirty(true);
    onRowsChange?.(original, rows);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    // Filter out blank-name rows to match the legacy server-side behavior, and
    // drop the UI-only `cenaSDph` mirror — only `cena` (bez DPH) is persisted.
    const cleanOriginal = original
      .filter((r) => r.nazev.trim().length > 0)
      .map(({ doekod, nazev, cena, id_dph }) => ({ doekod, nazev, cena, id_dph }));
    const cleanNeoriginal = neoriginal
      .filter((r) => r.nazev.trim().length > 0)
      .map(({ nazev, cena, id_dph }) => ({ nazev, cena, id_dph }));
    onSubmit({ original: cleanOriginal, neoriginal: cleanNeoriginal });
  };

  const rates = vatRates.data?.rates ?? [];
  const originalEditor = (
    <EquipmentRowsOriginal rows={original} vatRates={rates} onChange={changeOriginal} variant={variant} />
  );
  const neoriginalEditor = (
    <EquipmentRowsNeoriginal rows={neoriginal} vatRates={rates} onChange={changeNeoriginal} variant={variant} />
  );

  return (
    <form id={EQUIPMENT_FORM_ID} onSubmit={submit} className="flex flex-col gap-6">
      {variant === 'wizard' ? (
        <>
          <FormPanel>
            <SectionHeader
              number={3}
              title={<>Originální <em>výbava</em>.</>}
              description="Položky se po uložení přepíší jako celek — odebrání řádku znamená jeho smazání."
            />
            {originalEditor}
          </FormPanel>
          <FormPanel>
            <SectionHeader
              number={4}
              title={<>Dodatečná <em>výbava</em>.</>}
              description="Neoriginální položky — bez kódu."
            />
            {neoriginalEditor}
          </FormPanel>
        </>
      ) : (
        <>
          <section>
            <h3 className="mb-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3">
              Originální výbava
            </h3>
            {originalEditor}
          </section>
          <section>
            <h3 className="mb-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3">
              Dodatečná výbava
            </h3>
            {neoriginalEditor}
          </section>
        </>
      )}
    </form>
  );
}

function EquipmentRowsOriginal({
  rows,
  vatRates,
  onChange,
  variant,
}: {
  rows: OriginalRow[];
  vatRates: VatRate[];
  onChange: (rows: OriginalRow[]) => void;
  variant: EquipmentVariant;
}) {
  const update = (i: number, patch: Partial<OriginalRow>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, emptyOriginalRow()]);

  return (
    <div className="space-y-3">
      {rows.length === 0 &&
        (variant === 'wizard' ? (
          <EmptyState
            eyebrow="Beze záznamů"
            title="Zatím žádná originální výbava"
            description='Použijte tlačítko "Přidat položku" pro vložení prvního řádku.'
          />
        ) : (
          <p className="text-[12px] italic text-ink-3">Zatím žádná originální výbava.</p>
        ))}
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[80px_1fr_130px_130px_150px_36px] items-end gap-2 border border-line p-3 max-sm:grid-cols-2"
          aria-label={`Originální položka ${i + 1}`}
        >
          <Field label="Kód">
            <TextInput value={r.doekod} onChange={(e) => update(i, { doekod: e.currentTarget.value })} />
          </Field>
          <Field label="Název">
            <TextInput value={r.nazev} onChange={(e) => update(i, { nazev: e.currentTarget.value })} />
          </Field>
          <EquipmentPriceFields value={r} vatRates={vatRates} onChange={(next) => update(i, next)} />
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="close" size={12} />}
            aria-label="Odebrat položku"
            onClick={() => remove(i)}
          />
        </div>
      ))}
      <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={add}>
        Přidat položku
      </Button>
    </div>
  );
}

function EquipmentRowsNeoriginal({
  rows,
  vatRates,
  onChange,
  variant,
}: {
  rows: NonOriginalRow[];
  vatRates: VatRate[];
  onChange: (rows: NonOriginalRow[]) => void;
  variant: EquipmentVariant;
}) {
  const update = (i: number, patch: Partial<NonOriginalRow>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, emptyNonOriginalRow()]);

  return (
    <div className="space-y-3">
      {rows.length === 0 &&
        (variant === 'wizard' ? (
          <EmptyState
            eyebrow="Beze záznamů"
            title="Zatím žádná dodatečná výbava"
            description='Použijte tlačítko "Přidat položku" pro vložení prvního řádku.'
          />
        ) : (
          <p className="text-[12px] italic text-ink-3">Zatím žádná dodatečná výbava.</p>
        ))}
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_130px_130px_150px_36px] items-end gap-2 border border-line p-3 max-sm:grid-cols-2"
          aria-label={`Dodatečná položka ${i + 1}`}
        >
          <Field label="Název">
            <TextInput value={r.nazev} onChange={(e) => update(i, { nazev: e.currentTarget.value })} />
          </Field>
          <EquipmentPriceFields value={r} vatRates={vatRates} onChange={(next) => update(i, next)} />
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="close" size={12} />}
            aria-label="Odebrat položku"
            onClick={() => remove(i)}
          />
        </div>
      ))}
      <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={add}>
        Přidat položku
      </Button>
    </div>
  );
}

// ─── Discount ───────────────────────────────────────────────────────────────
//
// Three linked fields:
//   - sleva_procenta (% — percentage of zakladni_cena)
//   - sleva_bez_dph  (Kč — absolute amount, bez DPH)
//   - sleva_s_dph    (Kč — absolute amount, including DPH; UI helper)
// bez DPH ⇄ včetně DPH auto-convert in realtime via the record's VAT rate;
// editing either clears the % field (and vice versa). Only sleva_procenta and
// sleva_bez_dph are persisted — the server derives the rest.
//
// sleva_zakladni_cena stores both discount kinds in one column (≤100 = %,
// >100 = absolute Kč; see nabidka-helpers).

export const DISCOUNT_FORM_ID = 'vozidla-discount-form';

export interface DiscountValues {
  percent: string;
  bezDph: string;
  sDph: string;
}

export function DiscountFormBody({
  storedSleva,
  dphMult,
  seedReady = true,
  busy,
  onSubmit,
  onValuesChange,
  fleetPolicyNabidkaId,
  totals,
}: {
  /** Stored `sleva_zakladni_cena` (≤100 = %, >100 = absolute Kč — see nabidka-helpers). */
  storedSleva: number;
  dphMult: number;
  /** Gate seeding until the VAT rate is known (the wizard waits for its summary). */
  seedReady?: boolean;
  busy: boolean;
  onSubmit: (body: { sleva_procenta: number | null; sleva_bez_dph: number | null }) => void;
  /** Mirrors field values up for chrome-side live previews (wizard aside). */
  onValuesChange?: (values: DiscountValues) => void;
  /** Wizard: renders the fleet-policy decision panel below the fields. */
  fleetPolicyNabidkaId?: number;
  /** Modal: renders the price recap rows below the fields. */
  totals?: { zakladniCena: number; storedIsPercent: boolean; cenaCelkem: number };
}) {
  const [percent, setPercent] = useState('');
  const [bezDph, setBezDph] = useState('');
  const [sDph, setSDph] = useState('');
  // Re-seed from the stored discount until the user starts editing — a
  // background refetch's fresh value replaces a stale seed (see the
  // equipment body comment for the failure mode a one-time seed causes).
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!seedReady || dirty) return;
    let next: DiscountValues;
    if (storedSleva > 100) {
      next = {
        percent: '',
        bezDph: storedSleva.toFixed(2),
        sDph: (storedSleva * dphMult).toFixed(2),
      };
    } else if (storedSleva > 0) {
      next = { percent: String(storedSleva), bezDph: '', sDph: '' };
    } else {
      next = { percent: '', bezDph: '', sDph: '' };
    }
    setPercent(next.percent);
    setBezDph(next.bezDph);
    setSDph(next.sDph);
    onValuesChange?.(next);
    // `onValuesChange` is a mirror callback — the seed inputs are the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedReady, storedSleva, dphMult, dirty]);

  const apply = (next: DiscountValues) => {
    setDirty(true);
    setPercent(next.percent);
    setBezDph(next.bezDph);
    setSDph(next.sDph);
    onValuesChange?.(next);
  };
  const onBezDph = (val: string) =>
    apply({
      percent: '',
      bezDph: val,
      sDph: val.trim() ? ((parseFloat(val) || 0) * dphMult).toFixed(2) : '',
    });
  const onSDph = (val: string) =>
    apply({
      percent: '',
      bezDph: val.trim() ? ((parseFloat(val) || 0) / dphMult).toFixed(2) : '',
      sDph: val,
    });
  const onPercent = (val: string) => apply({ percent: val, bezDph: '', sDph: '' });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit({
      sleva_procenta: percent.trim() ? Number(percent) : null,
      sleva_bez_dph: bezDph.trim() ? Number(bezDph) : null,
    });
  };

  return (
    <form id={DISCOUNT_FORM_ID} onSubmit={submit}>
      <Fields cols={3}>
        <Field label="Sleva procenta">
          <TextInput type="number" value={percent} onChange={(e) => onPercent(e.currentTarget.value)} suffix="%" />
        </Field>
        <Field label="Sleva bez DPH">
          <TextInput type="number" value={bezDph} onChange={(e) => onBezDph(e.currentTarget.value)} suffix="Kč" />
        </Field>
        <Field label="Sleva včetně DPH">
          <TextInput type="number" value={sDph} onChange={(e) => onSDph(e.currentTarget.value)} suffix="Kč" />
        </Field>
      </Fields>
      {fleetPolicyNabidkaId != null && (
        <div className="mt-6">
          <FleetPolicyPanel
            nabidkaId={fleetPolicyNabidkaId}
            showEmpty
            onApplyPercent={(p) => onPercent(String(p))}
            onApplyKc={(kc) => onBezDph(String(kc))}
            onApplyKcSDph={(kc) => onSDph(String(kc))}
          />
        </div>
      )}
      {totals && (
        <div className="mt-5 flex flex-col gap-1.5 border-t border-line pt-4">
          <Row k="Základní cena" v={`${formatInt(totals.zakladniCena)} Kč`} />
          <Row
            k="Předchozí sleva"
            v={totals.storedIsPercent ? `${storedSleva} %` : `${formatInt(storedSleva)} Kč`}
          />
          <Row k="Cena celkem" v={`${formatInt(Number(totals.cenaCelkem || 0))} Kč`} strong />
        </div>
      )}
    </form>
  );
}

// ─── Financing ──────────────────────────────────────────────────────────────

export const FINANCING_FORM_ID = 'vozidla-financing-form';

export interface FinancingFormValue {
  typ_financovani: number;
  misto_predani: number;
  /** `YYYY-MM-DD` or '' — see dateInputValue. */
  datum_predani: string;
}

/**
 * Clip a MySQL DATETIME ("YYYY-MM-DD HH:mm:ss") to the date part
 * `<input type="date">` expects, tolerating the empty sentinel "0000-00-00".
 */
export function dateInputValue(raw: string | null | undefined): string {
  if (!raw || raw.startsWith('0000')) return '';
  return raw.slice(0, 10);
}

/**
 * Financing + handover fields. The wrapper owns the payload mapping — the
 * offer surfaces clear deselected values with `|| null` while the contract
 * modal sends `|| undefined` (documented in the *EditModals files).
 */
export function FinancingFormBody({
  initial,
  busy,
  onSubmit,
  onValuesChange,
}: {
  initial: FinancingFormValue;
  busy: boolean;
  onSubmit: (form: FinancingFormValue) => void;
  /** Mirrors field values up for chrome-side summaries (wizard aside). */
  onValuesChange?: (form: FinancingFormValue) => void;
}) {
  const types = useFinancingTypesQuery();
  const places = useDeliveryPlacesQuery();

  const [form, setForm] = useState(initial);
  const [dirty, setDirty] = useState(false);
  // Re-seed from fresh data until the user starts editing.
  useEffect(() => {
    if (dirty) return;
    setForm(initial);
    onValuesChange?.(initial);
    // `onValuesChange` is a mirror callback — the seed input is the dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, dirty]);

  const set = <K extends keyof FinancingFormValue>(k: K, v: FinancingFormValue[K]) => {
    const next = { ...form, [k]: v };
    setDirty(true);
    setForm(next);
    onValuesChange?.(next);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit(form);
  };

  return (
    <form id={FINANCING_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        <Field label="Typ financování">
          <SelectInput
            value={String(form.typ_financovani)}
            onChange={(e) => set('typ_financovani', Number(e.currentTarget.value) || 0)}
          >
            <option value="0">— Vyberte —</option>
            {(types.data?.types ?? []).map((t) => (
              <option key={t.id_financovani} value={t.id_financovani}>
                {t.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Místo předání">
          <SelectInput
            value={String(form.misto_predani)}
            onChange={(e) => set('misto_predani', Number(e.currentTarget.value) || 0)}
          >
            <option value="0">— Vyberte —</option>
            {(places.data?.places ?? []).map((p) => (
              <option key={p.id_mista} value={p.id_mista}>
                {p.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Datum předání">
          <TextInput
            type="date"
            value={form.datum_predani}
            onChange={(e) => set('datum_predani', e.currentTarget.value)}
          />
        </Field>
      </Fields>
    </form>
  );
}

// ─── Shared bits ────────────────────────────────────────────────────────────

export function Row({ k, v, strong = false }: { k: string; v: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">{k}</span>
      <span
        className={
          strong
            ? 'font-mono text-[15px] font-semibold tracking-[-0.02em] text-ink'
            : 'font-mono text-[12px] text-ink'
        }
      >
        {v}
      </span>
    </div>
  );
}
